import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

/**
 * /forgot-password — single email input + generic success acknowledgement.
 *
 * Hardened against email enumeration: the API ALWAYS returns 200 + ok:true
 * regardless of whether the email matches a user. We render the same
 * success state for every successful POST, so the UI also cannot leak
 * existence. The "If an account exists…" copy makes the intent explicit
 * to the legitimate user.
 */
export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSubmitted(true);
        } catch (err) {
            // Only the 422 validation case (e.g. badly-formed email) gets
            // a real error. Everything else MUST surface as success per
            // the enumeration-protection contract.
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <AuthLayout
                eyebrow="Forgot password"
                title="Check your email"
                subtitle="If an account exists for this email, a password reset link has been sent. It expires in 60 minutes."
                footer={
                    <Link
                        to="/login"
                        data-testid="forgot-password-back-to-login"
                        className="font-medium text-blue-900 hover:underline"
                    >
                        Back to sign in
                    </Link>
                }
            >
                <div
                    className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
                    data-testid="forgot-password-success-banner"
                >
                    If an account exists for <strong>{email}</strong>, a password reset link has
                    been sent. Check your inbox — and your spam folder, just in case.
                </div>
                <p className="mt-4 text-xs text-slate-500">
                    Didn&apos;t receive the email? You can{" "}
                    <button
                        type="button"
                        onClick={() => { setSubmitted(false); setEmail(""); }}
                        data-testid="forgot-password-retry-link"
                        className="underline decoration-dotted hover:decoration-solid text-blue-900"
                    >
                        try a different email
                    </button>{" "}
                    or wait a moment and request another reset link.
                </p>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            eyebrow="Forgot password"
            title="Reset your password"
            subtitle="Enter the email address associated with your account and we'll send you a link to reset your password."
            footer={
                <>
                    Remembered it?{" "}
                    <Link
                        to="/login"
                        data-testid="forgot-password-back-to-login"
                        className="font-medium text-blue-900 hover:underline"
                    >
                        Back to sign in
                    </Link>
                </>
            }
        >
            <form
                onSubmit={onSubmit}
                className="space-y-5"
                data-testid="forgot-password-form"
            >
                <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                        id="forgot-email"
                        data-testid="forgot-password-email-input"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(ev) => setEmail(ev.target.value)}
                        placeholder="you@company.com"
                    />
                </div>
                {error && (
                    <div
                        data-testid="forgot-password-error"
                        className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100"
                    >
                        {error}
                    </div>
                )}
                <Button
                    type="submit"
                    data-testid="forgot-password-submit-button"
                    disabled={submitting}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white h-11"
                >
                    {submitting ? "Sending…" : "Send reset link"}
                </Button>
            </form>
        </AuthLayout>
    );
}
